import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface ContentFixResult {
  totalCorrupted: number;
  fixed: number;
  failed: number;
  moduleIds: string[];
}

export interface FixProgress {
  operationId: string;
  phase: 'identifying' | 'regenerating' | 'audio' | 'complete' | 'retrying';
  processed: number;
  total: number;
  successful: number;
  failed: number;
  currentBatch: number;
  totalBatches: number;
  retryAttempt?: number;
  maxRetries?: number;
}

type ProgressCallback = (progress: FixProgress) => void;

const BATCH_SIZE = 10;
const MAX_AUTO_RETRIES = 3;
const RETRY_DELAY_BASE = 5000; // 5 seconds base delay

/**
 * Identify modules with corrupted content (AI refusal messages, placeholders, undefined)
 */
export async function identifyCorruptedContent(): Promise<{ moduleId: string; courseId: string; issue: string }[]> {
  try {
    const { data: modules, error } = await supabase
      .from('module_content')
      .select('module_id, course_id, content')
      .or('content.ilike.%I cannot%,content.ilike.%I apologize%,content.ilike.%undefined%,content.ilike.%placeholder%,content.ilike.%Coming soon%');

    if (error) throw error;

    // Deduplicate by module_id
    const seen = new Set<string>();
    const corrupted = (modules || [])
      .filter(m => {
        if (seen.has(m.module_id)) return false;
        seen.add(m.module_id);
        return true;
      })
      .map(m => ({
        moduleId: m.module_id,
        courseId: m.course_id,
        issue: m.content?.substring(0, 100) || 'Empty content'
      }));

    logger.info(`Found ${corrupted.length} modules with corrupted content`);
    return corrupted;
  } catch (error) {
    logger.error('Failed to identify corrupted content:', error);
    throw error;
  }
}

/**
 * Process a single batch with retry logic
 */
async function processBatchWithRetry(
  batch: string[],
  operationId: string,
  maxRetries: number = 3
): Promise<{ successful: number; failed: number; failedIds: string[] }> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const { data, error } = await supabase.functions.invoke('regenerate-module-content', {
        body: { 
          moduleIds: batch,
          operationId 
        }
      });

      if (error) {
        throw new Error(error.message || 'Edge function error');
      }

      const failedIds = (data?.results || [])
        .filter((r: { success: boolean }) => !r.success)
        .map((r: { moduleId: string }) => r.moduleId);

      return {
        successful: data?.successful || 0,
        failed: data?.failed || 0,
        failedIds
      };
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      logger.warn(`Batch attempt ${attempt + 1}/${maxRetries} failed: ${lastError.message}`);
      
      if (attempt < maxRetries - 1) {
        const delay = RETRY_DELAY_BASE * Math.pow(2, attempt);
        logger.info(`Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  logger.error(`Batch failed after ${maxRetries} attempts:`, lastError);
  return {
    successful: 0,
    failed: batch.length,
    failedIds: batch
  };
}

/**
 * Regenerate content for modules with self-healing auto-retry
 */
export async function triggerContentRegeneration(
  moduleIds: string[],
  onProgress?: ProgressCallback,
  enableAutoRetry: boolean = true
): Promise<ContentFixResult> {
  try {
    logger.info(`🔧 Starting content regeneration for ${moduleIds.length} modules...`);

    // Create operation record for tracking
    const { data: operation, error: opError } = await supabase
      .from('content_enhancement_operations')
      .insert({
        operation_type: 'content_regeneration',
        status: 'running',
        total_items: moduleIds.length,
        processed_items: 0,
        successful_items: 0,
        failed_items: 0,
        started_at: new Date().toISOString(),
      })
      .select('id')
      .single();

    if (opError) {
      logger.error('Failed to create operation record:', opError);
    }

    const operationId = operation?.id || 'unknown';
    let totalSuccessful = 0;
    let totalFailed = 0;
    let remainingModules = [...moduleIds];
    let retryAttempt = 0;

    // Main processing loop with auto-retry for failed modules
    while (remainingModules.length > 0 && (!enableAutoRetry || retryAttempt <= MAX_AUTO_RETRIES)) {
      const isRetry = retryAttempt > 0;
      
      if (isRetry) {
        logger.info(`🔄 Auto-retry attempt ${retryAttempt}/${MAX_AUTO_RETRIES} for ${remainingModules.length} failed modules`);
        onProgress?.({
          operationId,
          phase: 'retrying',
          processed: totalSuccessful + totalFailed,
          total: moduleIds.length,
          successful: totalSuccessful,
          failed: totalFailed,
          currentBatch: 0,
          totalBatches: Math.ceil(remainingModules.length / BATCH_SIZE),
          retryAttempt,
          maxRetries: MAX_AUTO_RETRIES,
        });
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_BASE * retryAttempt));
      }

      const failedInThisPass: string[] = [];
      const totalBatches = Math.ceil(remainingModules.length / BATCH_SIZE);
      
      for (let i = 0; i < remainingModules.length; i += BATCH_SIZE) {
        const batch = remainingModules.slice(i, i + BATCH_SIZE);
        const currentBatch = Math.floor(i / BATCH_SIZE) + 1;

        logger.info(`Processing batch ${currentBatch}/${totalBatches} (${batch.length} modules)`);

        onProgress?.({
          operationId,
          phase: isRetry ? 'retrying' : 'regenerating',
          processed: totalSuccessful + totalFailed,
          total: moduleIds.length,
          successful: totalSuccessful,
          failed: totalFailed,
          currentBatch,
          totalBatches,
          retryAttempt: isRetry ? retryAttempt : undefined,
          maxRetries: isRetry ? MAX_AUTO_RETRIES : undefined,
        });

        const result = await processBatchWithRetry(batch, operationId, 2);
        
        totalSuccessful += result.successful;
        
        if (result.failedIds.length > 0) {
          failedInThisPass.push(...result.failedIds);
        }

        logger.info(`Batch ${currentBatch} complete: ${result.successful} successful, ${result.failed} failed`);

        // Update operation record
        if (operation?.id) {
          await supabase
            .from('content_enhancement_operations')
            .update({
              processed_items: totalSuccessful + failedInThisPass.length,
              successful_items: totalSuccessful,
              failed_items: failedInThisPass.length,
              updated_at: new Date().toISOString(),
            })
            .eq('id', operation.id);
        }

        // Small delay between batches
        if (i + BATCH_SIZE < remainingModules.length) {
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      }

      // Update for next iteration
      remainingModules = failedInThisPass;
      totalFailed = failedInThisPass.length;
      
      if (remainingModules.length === 0) {
        logger.info(`✅ All modules processed successfully!`);
        break;
      }
      
      if (enableAutoRetry && remainingModules.length > 0) {
        retryAttempt++;
      } else {
        break;
      }
    }

    // Mark operation complete
    if (operation?.id) {
      await supabase
        .from('content_enhancement_operations')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          processed_items: moduleIds.length,
          successful_items: totalSuccessful,
          failed_items: totalFailed,
        })
        .eq('id', operation.id);
    }

    onProgress?.({
      operationId,
      phase: 'complete',
      processed: moduleIds.length,
      total: moduleIds.length,
      successful: totalSuccessful,
      failed: totalFailed,
      currentBatch: Math.ceil(moduleIds.length / BATCH_SIZE),
      totalBatches: Math.ceil(moduleIds.length / BATCH_SIZE),
    });

    logger.info(`✅ Content regeneration complete: ${totalSuccessful} fixed, ${totalFailed} failed after ${retryAttempt} retries`);
    
    return {
      totalCorrupted: moduleIds.length,
      fixed: totalSuccessful,
      failed: totalFailed,
      moduleIds
    };
  } catch (error) {
    logger.error('Failed to trigger content regeneration:', error);
    throw error;
  }
}

/**
 * Generate missing audio for specific modules
 */
export async function generateMissingAudio(moduleIds: string[]): Promise<{ success: boolean; processed: number }> {
  try {
    logger.info(`🎧 Generating audio for ${moduleIds.length} modules...`);

    const { data, error } = await supabase.functions.invoke('direct-audio-generator', {
      body: { moduleIds }
    });

    if (error) {
      logger.error('Audio generation failed:', error);
      return { success: false, processed: 0 };
    }

    logger.info('✅ Audio generation complete:', data);
    return { success: true, processed: data?.processed || moduleIds.length };
  } catch (error) {
    logger.error('Failed to generate audio:', error);
    return { success: false, processed: 0 };
  }
}

/**
 * Get list of modules missing audio
 */
export async function getModulesMissingAudio(): Promise<string[]> {
  try {
    const { data: modulesWithAudio } = await supabase
      .from('module_audio')
      .select('module_id');

    const audioModuleIds = new Set((modulesWithAudio || []).map(m => m.module_id));

    const { data: allModules } = await supabase
      .from('course_modules')
      .select('module_id')
      .eq('is_enabled', true);

    const missingAudio = (allModules || [])
      .filter(m => !audioModuleIds.has(m.module_id))
      .map(m => m.module_id);

    logger.info(`Found ${missingAudio.length} modules missing audio`);
    return missingAudio;
  } catch (error) {
    logger.error('Failed to get modules missing audio:', error);
    return [];
  }
}

/**
 * Run complete content fix pipeline with progress tracking
 */
export async function runCompleteFix(
  onProgress?: ProgressCallback,
  enableAutoRetry: boolean = true
): Promise<{
  contentFix: ContentFixResult;
  audioFix: { success: boolean; processed: number };
}> {
  // Phase 1: Identify corrupted content
  onProgress?.({
    operationId: 'complete-fix',
    phase: 'identifying',
    processed: 0,
    total: 0,
    successful: 0,
    failed: 0,
    currentBatch: 0,
    totalBatches: 0,
  });

  const corrupted = await identifyCorruptedContent();
  const moduleIds = corrupted.map(c => c.moduleId);
  
  // Phase 2: Fix corrupted content with auto-retry
  const contentFix = moduleIds.length > 0 
    ? await triggerContentRegeneration(moduleIds, onProgress, enableAutoRetry)
    : { totalCorrupted: 0, fixed: 0, failed: 0, moduleIds: [] };

  // Phase 3: Generate missing audio
  onProgress?.({
    operationId: 'complete-fix',
    phase: 'audio',
    processed: 0,
    total: 0,
    successful: contentFix.fixed,
    failed: contentFix.failed,
    currentBatch: 0,
    totalBatches: 0,
  });

  const missingAudioModules = await getModulesMissingAudio();
  const audioFix = missingAudioModules.length > 0
    ? await generateMissingAudio(missingAudioModules)
    : { success: true, processed: 0 };

  onProgress?.({
    operationId: 'complete-fix',
    phase: 'complete',
    processed: moduleIds.length,
    total: moduleIds.length,
    successful: contentFix.fixed,
    failed: contentFix.failed,
    currentBatch: 0,
    totalBatches: 0,
  });

  return { contentFix, audioFix };
}
